import React, { useEffect, useState } from "react";
import MessageItem from "./MessageItem";
import {
  groupMessagesByDateChronologically,
  resolveAuthor,
  THREAD_REPLY_PLATFORMS,
} from "./Conversation.utils";
import LoadingComponent from "../../components/common/LoadingComponent";
// import PlatformDropdown from "./PlatformDropdown";
// import { Icons } from "../../constants/icon-library";
import { useConversationContext } from "./ConversationContext";
import { MESSAGE_GROUPED_PLATFORMS } from "../../constants/constants";

function ThreadColumn({ threadData }) {
  const { meta, authorMap } = useConversationContext();
  const { isAllConversations, isMailIntegration, isCommentIntegration, sendingMessage } = meta;

  const {
    selectedParticipant,
    selectedParticipantMessages,
    taskActions,
    replyProps,
    platformProps,
    messageInput,
    isComposerOpen,
    closeComposer,
  } = threadData;

  const { draft, setDraft, onSend, onKeyDown } = messageInput;

  const headerMsg = selectedParticipantMessages?.[0] || null;
  const msgId = headerMsg ? String(headerMsg.externalMessageId) : null;
  const participantKey = selectedParticipant
    ? `${selectedParticipant.integrationKey}:${selectedParticipant.participantKey}`
    : null;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [participantKey]);

  const canSendToChannel = !isAllConversations && !isMailIntegration && !isCommentIntegration;

  const SendIcon = ({ sending }) =>
    sending ? (
      <span className="conv-spinner-wrap">
        <LoadingComponent
          loading={true}
          size={6}
          wrapperStyle={{
            padding: 0,
            lineHeight: 0,
            display: "flex",
            flexDirection: "row",
            whiteSpace: "nowrap",
          }}
        />
      </span>
    ) : (
      <i className="bi bi-send-fill" />
    );

  const renderComposer = () => {
    if (headerMsg && replyProps.replyOpenId === msgId) {
      const authorName = resolveAuthor(
        headerMsg.author?.name,
        authorMap,
      );
      return (
        <div className="border-top border-secondary-subtle px-4 py-3 flex-shrink-0 bg-body">
          <div className="input-group conv-composer-group">
            <input
              type="text"
              className="form-control border-0 bg-body-secondary text-body rounded-start-pill ps-3 conv-composer-input"
              placeholder={`Reply to ${authorName}`}
              value={replyProps.replyDrafts[msgId] || ""}
              onChange={(e) => replyProps.onReplyChange(msgId, e.target.value)}
              disabled={replyProps.sendingReplyIds?.has(msgId)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  replyProps.onSendReply(headerMsg, msgId);
                }
              }}
              autoFocus
            />
            <button
              className="btn btn-primary rounded-end-pill px-3 d-flex align-items-center justify-content-center conv-composer-send"
              type="button"
              onClick={() => replyProps.onSendReply(headerMsg, msgId)}
              disabled={
                !replyProps.replyDrafts[msgId]?.trim() ||
                replyProps.sendingReplyIds?.has(msgId)
              }
            >
              <SendIcon sending={replyProps.sendingReplyIds?.has(msgId)} />
            </button>
          </div>
        </div>
      );
    }

    if (isComposerOpen && canSendToChannel) {
      return (
        <div className="border-top border-secondary-subtle px-4 py-3 flex-shrink-0 bg-body">
          <div className="input-group conv-composer-group">
            <input
              type="text"
              className="form-control border-0 bg-body-secondary text-body rounded-start-pill ps-3 conv-composer-input"
              placeholder="Message"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={sendingMessage}
              autoFocus
            />
            <button
              className="btn btn-primary rounded-end-pill px-3 d-flex align-items-center justify-content-center conv-composer-send"
              type="button"
              onClick={onSend}
              disabled={!draft.trim() || sendingMessage}
            >
              <SendIcon sending={sendingMessage} />
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  if (!selectedParticipant || !selectedParticipantMessages?.length) {
    return (
      <div className="d-flex flex-column border-end border-secondary-subtle conv-thread-column">
        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-body-secondary px-3 conv-thread-placeholder">
          <i className="bi bi-chat-square-text fs-1 mb-3" />
          <h6>Select a message</h6>
          <small>Choose a message from the list to view its thread</small>
        </div>
        {renderComposer()}
      </div>
    );
  }

  const authorName = resolveAuthor(
    headerMsg.author?.name,
    authorMap,
  );
  const {
    addedMessageIds,
    addingTaskId,
    justAdded,
    onAddTask,
    onAddThreadTask,
  } = taskActions;

  const alreadyAdded = !!msgId && addedMessageIds.has(msgId);
  const isAdding = addingTaskId === msgId;
  const showCheck = justAdded === msgId || alreadyAdded;
  const isReplyOpen = replyProps.replyOpenId === msgId;

  const messagePlatform = (
    headerMsg?.platform ||
    meta.platform ||
    meta.group?.platform ||
    ""
  ).toLowerCase();

  const canReplyToThreadItems =
    THREAD_REPLY_PLATFORMS.includes(messagePlatform);

  const canReplyToHeaderMessage =
    MESSAGE_GROUPED_PLATFORMS.includes(messagePlatform);

  const buildThreadStates = (item) => {
    const taskState = {};
    const replyState = {};
    (item.threads || []).forEach((thread) => {
      const tId = thread.externalMessageId;
      if (!tId) return;
      taskState[tId] = {
        alreadyAdded: addedMessageIds.has(tId),
        isAdding: addingTaskId === tId,
        showCheck: justAdded === tId || addedMessageIds.has(tId),
      };
      replyState[tId] = {
        open: replyProps.replyOpenId === tId,
        draft: replyProps.replyDrafts[tId],
        isSending: replyProps.sendingReplyIds?.has(tId),
      };
    });
    return { taskState, replyState };
  };

  // const msgPlatformProps = {
  //   platforms: platformProps.platforms,
  //   platformMenuOpen: platformProps.platformMenuOpenId === msgId,
  //   onTogglePlatformMenu: () => platformProps.onTogglePlatformMenu(msgId),
  //   onSelectPlatform: (platform) =>
  //     platformProps.onSelectPlatform(headerMsg, authorName, platform),
  // };

  const handleReplyClick = () => {
    if (isComposerOpen) closeComposer();
    replyProps.onToggleReply(msgId);
  };

  const conversationMessageDateGroups = groupMessagesByDateChronologically(
    selectedParticipantMessages,
  );

  return (
    <div className="d-flex flex-column border-end border-secondary-subtle conv-thread-column">
      <div className="d-flex align-items-center justify-content-end gap-2 px-4 py-2 flex-shrink-0 border-bottom border-secondary-subtle bg-body">
        <button
          type="button"
          className="btn p-0 bg-body shadow-sm border-0 conv-action-btn-md"
          title={
            isAllConversations
              ? "Select a channel to add a task"
              : alreadyAdded
                ? "Already added to tasks"
                : "Add to task"
          }
          onClick={() =>
            !alreadyAdded &&
            onAddTask(selectedParticipantMessages, authorName)
          }
          disabled={isAllConversations || isAdding || alreadyAdded}
        >
          {showCheck ? (
            <i className="bi bi-check-circle-fill text-success" />
          ) : (
            <i className="bi bi-plus-circle" />
          )}
        </button>

        {/* <div style={{ position: "relative" }}>
          <button
            type="button"
            className={`btn p-0 bg-body shadow-sm border-0 conv-action-btn-md ${
              !isAllConversations && !alreadyAdded ? "conv-btn-dim" : ""
            }`}
            title={
              isAllConversations
                ? "Select a channel to execute"
                : alreadyAdded
                  ? "Execute"
                  : "Add this message to tasks first, then execute"
            }
            onClick={msgPlatformProps.onTogglePlatformMenu}
            disabled={isAllConversations || !alreadyAdded}
          >
            <i
              className={`wb ${Icons("resume")} align-middle ${alreadyAdded ? "conv-icon-active" : ""}`}
            />
          </button>
          {msgPlatformProps.platformMenuOpen && (
            <PlatformDropdown
              platforms={msgPlatformProps.platforms}
              onSelect={msgPlatformProps.onSelectPlatform}
              onClose={msgPlatformProps.onTogglePlatformMenu}
            />
          )}
        </div> */}

        {canReplyToHeaderMessage && (
          <button
            type="button"
            className={`btn p-0 shadow-sm border-0 conv-action-btn-md ${isReplyOpen ? "bg-primary-subtle" : "bg-body"}`}
            title="Reply to this message"
            onClick={handleReplyClick}
          >
            <i className="bi bi-reply text-body-secondary" />
          </button>
        )}
      </div>

      <div
        key={participantKey}
        className={`flex-grow-1 px-4 py-3 conv-thread-scroll ${visible ? "conv-thread-scroll--visible" : ""}`}
      >
        {conversationMessageDateGroups.map((dateGroup) => (
          <div key={dateGroup.dateKey} className="d-flex flex-column">
            <div className="d-flex justify-content-center py-2">
              <span className="text-body-secondary fw-medium conv-date-label">
                {dateGroup.label}
              </span>
            </div>
            {dateGroup.messages.map((item) => {
              const itemMsgId = String(item.externalMessageId);
              const itemAuthorName = resolveAuthor(
                item.author?.name,
                authorMap,
              );
              const itemAlreadyAdded = addedMessageIds.has(itemMsgId);
              const itemIsAdding = addingTaskId === itemMsgId;
              const itemShowCheck =
                justAdded === itemMsgId || itemAlreadyAdded;
              const { taskState, replyState } = buildThreadStates(item);

              return (
                <MessageItem
                  key={itemMsgId}
                  messageData={{
                    msg: item,
                    authorName: itemAuthorName,
                    showGroupLabel: meta.showGroupLabel,
                    isAllConversations,
                  }}
                  taskActions={{
                    alreadyAdded: itemAlreadyAdded,
                    isAdding: itemIsAdding,
                    showCheck: itemShowCheck,
                    onAddTask,
                  }}
                  replyProps={{
                    replyOpen: replyProps.replyOpenId === itemMsgId,
                    replyDraft: replyProps.replyDrafts[itemMsgId],
                    onToggleReply: replyProps.onToggleReply,
                    onReplyChange: replyProps.onReplyChange,
                    onSendReply: replyProps.onSendReply,
                    isSendingReply: replyProps.sendingReplyIds?.has(itemMsgId),
                  }}
                  platformProps={{
                    platforms: platformProps.platforms,
                    platformMenuOpen:
                      platformProps.platformMenuOpenId === itemMsgId,
                    onTogglePlatformMenu: () =>
                      platformProps.onTogglePlatformMenu(itemMsgId),
                    onSelectPlatform: (platform) =>
                      platformProps.onSelectPlatform(
                        item,
                        itemAuthorName,
                        platform,
                      ),
                  }}
                  threadProps={{
                    threadTaskState: taskState,
                    onAddThreadTask,
                    threadReplyState: replyState,
                    onToggleThreadReply: replyProps.onToggleReply,
                    onThreadReplyChange: replyProps.onReplyChange,
                    onSendThreadReply: replyProps.onSendReply,
                    platforms: platformProps.platforms,
                    platformMenuOpenId: platformProps.platformMenuOpenId,
                    onToggleThreadPlatformMenu:
                      platformProps.onTogglePlatformMenu,
                    onSelectThreadPlatform: platformProps.onSelectPlatform,
                  }}
                  showActions={false}
                  flatConversation={false}
                  showThreadActions={canReplyToThreadItems}
                />
              );
            })}
          </div>
        ))}
      </div>

      {renderComposer()}
    </div>
  );
}

export default ThreadColumn;